import { useState } from "react"
import { router } from "@inertiajs/react"
import { Trash2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { InlineInput } from "@/components/ui/inline-input"
import { t } from "@/lib/i18n"

/**
 * The group's menu, and the one number that makes it safe to touch.
 *
 * **Every row says how far a price change reaches before you make it.** "Llega a
 * 18 · 2 con precio propio" is not decoration: a central price edit is the
 * highest-blast-radius control in the staff app, and an operator who cannot see
 * the radius before clicking is being asked to guess. The two figures are
 * counted across the WHOLE group, not across the branches this person can read —
 * the question is how many restaurants the change reaches, and hiding the ones
 * they lack membership in would under-report it.
 *
 * The price commits inline, per the updates-vs-creates rule: it is one light
 * operational field. Creating a central dish keeps an explicit submit, because a
 * name and a price only mean anything together.
 */

export type CentralItem = {
  id: number
  name: string
  description: string | null
  emoji: string | null
  price: string
  currency: string
  station: string
  status: string
  following: number
  overriding: number
}

function tCentral(key: string, options: Record<string, string | number> = {}) {
  return t(`ui.react.pages.staff.location.manage.central_items.index.${key}`, options)
}

function CentralRow({ item }: { item: CentralItem }) {
  return (
    <li className="flex flex-wrap items-center gap-3 px-4 py-3" data-testid={`central-item-${item.id}`}>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">
          {item.emoji ? `${item.emoji} ` : ""}
          {item.name}
        </span>
        {/* The blast radius, in words, beside the control that causes it. */}
        <span
          className="text-muted-foreground block text-xs tabular-nums"
          data-testid={`central-reach-${item.id}`}
        >
          {item.overriding > 0
            ? tCentral("reach_with_overrides", {
                following: item.following,
                overriding: item.overriding,
              })
            : tCentral("reach", { following: item.following })}
        </span>
      </span>

      <InlineInput
        value={item.price}
        inputMode="decimal"
        className="w-28"
        data-testid={`central-price-${item.id}`}
        onSubmit={async (value) => {
          router.patch(`/manage/central_items/${item.id}`, { price: value }, { preserveScroll: true })
        }}
      />

      <Button
        variant="ghost"
        size="icon-sm"
        aria-label={tCentral("remove", { name: item.name })}
        data-testid={`central-delete-${item.id}`}
        onClick={() => router.delete(`/manage/central_items/${item.id}`, { preserveScroll: true })}
      >
        <Trash2 className="size-4" aria-hidden="true" />
      </Button>
    </li>
  )
}

export function CentralMenu({
  items,
  branchCount,
}: {
  items: CentralItem[]
  branchCount: number
}) {
  const [name, setName] = useState("")
  const [price, setPrice] = useState("")

  const submit = () => {
    router.post(
      "/manage/central_items",
      { name, price },
      { preserveScroll: true, onSuccess: () => { setName(""); setPrice("") } }
    )
  }

  return (
    <Card density="compact" data-testid="central-menu">
      <CardHeader className="border-b">
        <CardTitle>{tCentral("title")}</CardTitle>
      </CardHeader>

      <CardContent className="space-y-4">
        <p className="text-muted-foreground text-sm" data-testid="central-branch-count">
          {tCentral("subtitle", { branches: branchCount })}
        </p>

        {items.length === 0 ? (
          <p className="text-muted-foreground text-sm" data-testid="central-empty">
            {tCentral("empty")}
          </p>
        ) : (
          <ul className="divide-border divide-y rounded-md border" data-testid="central-list">
            {items.map((item) => (
              <CentralRow key={item.id} item={item} />
            ))}
          </ul>
        )}

        {/* A create form with an explicit submit: a name and a price only mean
            anything together, and half of one is not a dish. */}
        <form
          className="flex flex-wrap items-end gap-2"
          data-testid="central-create"
          onSubmit={(event) => {
            event.preventDefault()
            submit()
          }}
        >
          <label className="flex min-w-40 flex-1 flex-col gap-1">
            <span className="text-muted-foreground text-xs">{tCentral("name_label")}</span>
            <input
              className="border-input h-11 rounded-md border px-3 text-sm md:h-9"
              value={name}
              onChange={(event) => setName(event.target.value)}
              data-testid="central-name-input"
            />
          </label>

          <label className="flex w-28 flex-col gap-1">
            <span className="text-muted-foreground text-xs">{tCentral("price_label")}</span>
            <input
              className="border-input h-11 rounded-md border px-3 text-sm tabular-nums md:h-9"
              inputMode="decimal"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              data-testid="central-price-input"
            />
          </label>

          <Button
            type="submit"
            className="h-11 md:h-9"
            disabled={name.trim() === "" || price.trim() === ""}
            data-testid="central-submit"
          >
            {tCentral("add")}
          </Button>
        </form>
      </CardContent>
    </Card>
  )
}
